import { Component, Input, OnChanges, OnInit, OnDestroy, Output, EventEmitter, SimpleChanges } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FieldInspectionService } from '../../services/field-inspection.service';
import { ReportListItem, InspectionStatus, ReportFilters } from '../../models/inspection-report.model';
import { ConfirmDeleteReportDialogComponent } from '../confirm-delete-report-dialog/confirm-delete-report-dialog.component';
import { HelperService } from '../../../helper.service';

interface SeasonGroup {
  seasonName: string;
  seasonId: number | null;
  reports: ReportListItem[];
  isActive: boolean;
}

@Component({
  selector: 'app-report-list',
  templateUrl: './report-list.component.html',
  standalone: false
})
export class ReportListComponent implements OnInit, OnChanges, OnDestroy {

  @Input() fieldId: number | null = null;
  @Input() farmId: number | null = null;
  @Input() tenantId: number | null = null;
  /** Scope list to a specific pin — the list then shows only that pin's inspections */
  @Input() pinId: number | null = null;
  /** Hide "+ New Inspection" when there is no pin context */
  @Input() hidePinRequiredActions: boolean = false;
  @Input() showFilters: boolean = false;
  /** Remove max-width constraint — use full available width */
  @Input() fullWidth: boolean = false;
  @Output() reportSelected = new EventEmitter<{ reportId: number; status: InspectionStatus }>();
  @Output() createNewInspection = new EventEmitter<void>();
  @Output() openExistingReport = new EventEmitter<{ reportId: string; viewOnly: boolean }>();

  filterForm: FormGroup;
  seasonGroups: SeasonGroup[] = [];
  /** The API refused the list (403), as opposed to returning no reports. */
  accessDenied = false;
  /** Report whose PDF is currently being requeued — blocks a second send while in flight */
  retriggeringId: string | null = null;

  private loadSub: Subscription;

  constructor(
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    public inspectionService: FieldInspectionService,
    public helper: HelperService
  ) {}

  ngOnInit(): void {
    if (!this.fieldId) {
      const p = this.route.snapshot.paramMap.get('fieldId');
      this.fieldId = p ? +p : null;
    }
    if (!this.farmId) {
      const p = this.route.snapshot.paramMap.get('farmId');
      this.farmId = p ? +p : null;
    }

    this.filterForm = this.fb.group({ status: [''], inspectorSearch: [''] });
    this.filterForm.valueChanges.subscribe(() => this.applyFilters());

    this.loadReports();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['pinId'] && !changes['pinId'].firstChange) {
      this.loadReports();
    }
  }

  ngOnDestroy(): void {
    this.loadSub?.unsubscribe();
  }

  private loadReports(): void {
    this.loadSub?.unsubscribe();

    const filters: ReportFilters = {};

    // Scope by pin on the server, so a busy field cannot push some of a pin's reports past the
    // page. fieldId/farmId still narrow the query, and are all that applies when there is no pin.
    if (this.pinId) {
      filters.pinId = this.pinId;
    }
    if (this.fieldId) {
      filters.fieldId = this.fieldId;
    } else if (this.farmId) {
      filters.farmId = this.farmId;
    }
    filters.page = 1;
    filters.size = 100;

    this.inspectionService.loading.set(true);
    this.accessDenied = false;
    this.loadSub = this.inspectionService.loadReportsObservable(filters).subscribe({
      next: (data) => {
        let reports = data.reports;

        // Fallback only: the API now filters by pinId, so this removes nothing. It keeps the list
        // correct if the web deploys ahead of a pin API that still ignores the parameter.
        // Loose equality to handle a string/number mismatch from the API.
        if (this.pinId) {
          // eslint-disable-next-line eqeqeq
          reports = reports.filter(r => r.pinId == this.pinId);
        }

        this.inspectionService.reportList.set(reports);
        // The API's total, not the length of this page: the count of the pin's or field's reports.
        this.inspectionService.totalReports.set(data.total ?? reports.length);
        this.inspectionService.loading.set(false);
        this.applyFilters();
      },
      error: (err) => {
        // A refused list used to read as "No inspection reports found", which is not the same thing.
        this.accessDenied = err?.status === 403;
        this.inspectionService.reportList.set([]);
        this.inspectionService.loading.set(false);
        this.applyFilters();
      }
    });
  }

  applyFilters(): void {
    if (!this.filterForm) return;
    const { status, inspectorSearch } = this.filterForm.value;
    let reports = [...this.inspectionService.reportList()];

    if (status) reports = reports.filter(r => r.status === status);
    if (inspectorSearch?.trim()) {
      const search = inspectorSearch.trim().toLowerCase();
      reports = reports.filter(r => r.inspectorName?.toLowerCase().includes(search));
    }

    this.groupBySeasonAndSort(reports);
  }

  private groupBySeasonAndSort(reports: ReportListItem[]): void {
    const map = new Map<number | null, SeasonGroup>();

    reports.forEach(report => {
      // Treat 0 and other falsy values the same as null — all become "Historical"
      const key = report.seasonId || null;
      if (!map.has(key)) {
        map.set(key, {
          seasonId: key as number,
          seasonName: key ? (report.seasonName ?? `Season ${key}`) : null,
          reports: [],
          isActive: this.isSeasonActive(report)
        });
      }
      map.get(key)!.reports.push(report);
    });

    const groups = Array.from(map.values());

    this.seasonGroups = groups.sort((a, b) => {
      // Active season always first
      if (a.isActive && !b.isActive) return -1;
      if (!a.isActive && b.isActive) return 1;
      // Then by seasonId descending (most recent season next)
      return (b.seasonId ?? -1) - (a.seasonId ?? -1);
    });
  }

  /**
   * Evaluated against the browser clock on every grouping pass rather than read
   * from the payload, so it stays correct as a season starts or ends without
   * needing a refetch.
   */
  private isSeasonActive(report: ReportListItem): boolean {
    if (!report.seasonStartDate || !report.seasonEndDate) return false;
    // end_date is exclusive platform-wide — a season whose end date is today has
    // already ended (see SeasonsService.endSeasons / season status calculation).
    const now = Date.now();
    return now >= new Date(report.seasonStartDate).getTime()
        && now < new Date(report.seasonEndDate).getTime();
  }

  openReport(report: ReportListItem): void {
    this.openExistingReport.emit({
      reportId: String(report.id),
      // Only an Inspector may edit. A requestor or viewer opening a draft gets it read-only
      // rather than an editable form whose save the API refuses.
      viewOnly: report.status === 'FINALISED' || !this.helper.canEditInspection()
    });
  }

  /**
   * Requeue the PDF for a finalised report. The endpoint fans the email out to every
   * Inspection Viewer in the tenant, so the button is held disabled for the duration
   * of the call — a double-click would re-send to all of them.
   */
  retriggerPdf(report: ReportListItem, event: MouseEvent): void {
    event.stopPropagation();
    if (this.retriggeringId) return;
    if (!this.helper.canEditInspection()) return;

    this.retriggeringId = report.id;
    this.inspectionService.retriggerPdf(String(report.id)).subscribe({
      next: (res) => {
        this.retriggeringId = null;
        // Generation is asynchronous — this confirms the request was queued, not delivery.
        this.snackBar.open(
          res?.message ?? 'PDF generation retriggered. It will be emailed once generated.',
          'Dismiss',
          { duration: 5000 }
        );
      },
      error: () => {
        this.retriggeringId = null;
        this.snackBar.open('Could not re-send the report. Please try again.', 'Dismiss', { duration: 4000 });
      }
    });
  }

  deleteReport(report: ReportListItem, event: MouseEvent): void {
    event.stopPropagation();
    if (!this.helper.canEditInspection()) return;

    const ref = this.dialog.open(ConfirmDeleteReportDialogComponent, {
      width: '380px',
      data: { reportDate: report.inspectionTimestamp }
    });

    ref.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;

      this.inspectionService.deleteReport(String(report.id)).subscribe({
        next: () => {
          // Remove from local signal list and re-render
          const updated = this.inspectionService.reportList().filter(r => r.id !== report.id);
          this.inspectionService.reportList.set(updated);
          this.inspectionService.totalReports.set(updated.length);
          this.applyFilters();
          this.snackBar.open('Inspection report deleted.', 'Dismiss', { duration: 3000 });
        },
        error: (err) => {
          const code = err?.error?.error?.code;
          if (code === 'REPORT_NOT_DRAFT') {
            this.snackBar.open('Only draft reports can be deleted.', 'Dismiss', { duration: 4000 });
          } else if (code === 'REPORT_NOT_FOUND') {
            this.snackBar.open('Report not found.', 'Dismiss', { duration: 4000 });
          } else if (err?.status === 403) {
            this.snackBar.open('You do not have permission to delete this report.', 'Dismiss', { duration: 4000 });
          } else {
            this.snackBar.open('Failed to delete report. Please try again.', 'Dismiss', { duration: 4000 });
          }
        }
      });
    });
  }

  clearFilters(): void {
    this.filterForm.reset({ status: '', inspectorSearch: '' });
  }

  getStatusClass(status: InspectionStatus): string {
    return status === 'FINALISED'
      ? 'bg-green-100 text-green-700'
      : 'bg-amber-100 text-amber-700';
  }
}
